import { TradeCard as ApiTradeCard, SetDetail, MatchIndexPackage } from 'store/services/trade/tradeApi';
import { TradeCard as ModalTradeCard } from '../components/InitiateTradeModal';

// Runtime-augmented card with auto-match metadata
export interface AutoMatchedCard extends ApiTradeCard {
  _autoMatchHaveCardId?: string;      // MTGCard.id (resolved from matchDetails.cardId)
  _autoMatchCompositeId?: string;     // Full compositeId for matchDetails lookups
}

// ── 1. Expansion Logic (v4-v6) ─────────────────────────────────────────────

export function expandAutoMatchCards(
  valuedCards: ApiTradeCard[],
  compositeIds: string[],
  matchDetails: Record<string, { condition: string; foil: boolean; cardId: number }> | undefined,
  haveListQuantityMap: Map<string, number>,
): AutoMatchedCard[] {
  // Filter cards to only those matching compositeIds
  const filtered = valuedCards
    .filter((card) => compositeIds.some((id) => id.startsWith(`${card.wantListId}-`)))
    .map((card) => ({ ...card, quantity: card.quantity || 1 }));

  // Build map: wantListId → haveCardId[]
  const wantToHaveMap = new Map<number, string[]>();
  for (const compositeId of compositeIds) {
    const [wantId, haveId] = compositeId.split('-');
    const wantIdNum = parseInt(wantId, 10);
    if (!wantToHaveMap.has(wantIdNum)) {
      wantToHaveMap.set(wantIdNum, []);
    }
    wantToHaveMap.get(wantIdNum)!.push(haveId);
  }

  // Build aggregate want qty map
  const wantQtyMap = new Map<string, number>();
  for (const card of filtered) {
    const cardName = card.name;
    wantQtyMap.set(cardName, (wantQtyMap.get(cardName) || 0) + card.quantity);
  }

  const expanded: AutoMatchedCard[] = [];

  for (const card of filtered) {
    const haveCardIds = wantToHaveMap.get(card.wantListId);

    if (!haveCardIds || haveCardIds.length === 0) {
      expanded.push(card);
      continue;
    }

    // Dedupe haveCardIds
    const seenHaveIds = new Set<string>();
    const uniqueHaveIds: string[] = [];
    for (const haveId of haveCardIds) {
      if (!seenHaveIds.has(haveId)) {
        seenHaveIds.add(haveId);
        uniqueHaveIds.push(haveId);
      }
    }

    const N = uniqueHaveIds.length;
    const senderHaveQty = haveListQuantityMap.get(card.name) || 0;
    const receiverWantQty = wantQtyMap.get(card.name) || card.quantity;
    const cap = Math.min(senderHaveQty, receiverWantQty);

    // Rule 2: Show all distinct versions — never hide potential sends
    if (N > cap && cap > 0) {
      for (const haveId of uniqueHaveIds) {
        const cId = `${card.wantListId}-${haveId}`;
        const details = matchDetails?.[cId];
        expanded.push({
          ...card,
          quantity: 1,
          foil: details?.foil ?? card.foil,
          _autoMatchHaveCardId: String(details?.cardId ?? haveId),
          _autoMatchCompositeId: cId,
        });
      }
    } else {
      // Normal distribution
      const baseQty = Math.floor(cap / N);
      const remainder = cap % N;

      for (let i = 0; i < uniqueHaveIds.length; i++) {
        const haveId = uniqueHaveIds[i];
        const cId = `${card.wantListId}-${haveId}`;
        const details = matchDetails?.[cId];
        const qty = baseQty + (i < remainder ? 1 : 0);

        if (qty > 0) {
          expanded.push({
            ...card,
            quantity: qty,
            foil: details?.foil ?? card.foil,
            _autoMatchHaveCardId: String(details?.cardId ?? haveId),
            _autoMatchCompositeId: cId,
          });
        }
      }
    }
  }

  return expanded;
}

// ── 2. Image Resolution (v5) ───────────────────────────────────────────────

export interface HaveMatchEntry {
  haveCardId: string;
  condition?: string;
  foil?: boolean;
}

export function resolveAutoMatchImageUrl(
  card: AutoMatchedCard,
  haveMatchMap?: Map<string, HaveMatchEntry>,
): string | undefined {
  // Check _autoMatchHaveCardId first (from expansion)
  const storedHaveCardId = card._autoMatchHaveCardId;

  if (storedHaveCardId && card.acceptableSetDetails) {
    const setDetail = card.acceptableSetDetails.find(
      (d) => String(d.cardId) === String(storedHaveCardId)
    );
    if (setDetail?.imageUrl) {
      return setDetail.imageUrl;
    }
  }

  // Fallback: Use haveMatchMap
  if (!haveMatchMap) return undefined;
  const haveMatch = haveMatchMap.get(String(card.wantListId));
  if (!haveMatch) return undefined;
  const setDetail = card.acceptableSetDetails?.find(
    (d) => String(d.cardId) === haveMatch.haveCardId
  );
  return setDetail?.imageUrl;
}

// ── 3. Build Have Match Map ────────────────────────────────────────────────

export function buildHaveMatchMapFromPackage(
  packageMatch: MatchIndexPackage | undefined,
): Map<string, HaveMatchEntry> {
  const map = new Map<string, HaveMatchEntry>();
  if (!packageMatch) return map;

  for (const compositeId of packageMatch.matchingCardIds) {
    const dashIdx = compositeId.indexOf('-');
    if (dashIdx === -1) continue;
    const wantListId = compositeId.substring(0, dashIdx);
    const details = packageMatch.matchDetails?.[compositeId];
    const haveCardId = String(details?.cardId ?? compositeId.substring(dashIdx + 1));
    if (!map.has(wantListId)) {
      map.set(wantListId, {
        haveCardId,
        condition: details?.condition,
        foil: details?.foil,
      });
    }
  }
  return map;
}

// ── 4. Build Modal Trade Card (v7) ─────────────────────────────────────────

export function buildModalTradeCard(
  card: AutoMatchedCard,
  haveMatch: HaveMatchEntry | undefined,
  getSetCode: (setName: string) => string,
  isUUID: (str: string) => boolean,
): ModalTradeCard {
  // Check price availability
  const hasNonFoil = !!(card.nonFoilPointValue && card.nonFoilPointValue > 0);
  const hasFoil = !!(card.foilPointValue && card.foilPointValue > 0);

  let acceptableSetDetails = card.acceptableSetDetails;

  // Only create our own acceptableSetDetails if backend doesn't provide it or it's empty
  if (!acceptableSetDetails || acceptableSetDetails.length === 0) {
    acceptableSetDetails = card.acceptableSets?.map((setName: string, index: number) => {
      const backendCode = (card as any).acceptableSetCodes?.[index];
      if (backendCode) {
        return { name: setName, code: backendCode, cardId: 0 };
      }
      const mappedCode = getSetCode(setName);
      if (mappedCode && mappedCode !== setName) {
        return { name: setName, code: mappedCode, cardId: 0 };
      }
      return { name: setName, code: setName, cardId: 0 };
    });
  }

  // Auto-match: find sender's specific printing in acceptableSetDetails
  let autoMatchSetDetail: SetDetail | undefined;
  if (haveMatch && acceptableSetDetails && acceptableSetDetails.length > 0) {
    autoMatchSetDetail = acceptableSetDetails.find(
      (d: SetDetail) => String(d.cardId) === haveMatch.haveCardId
    );
  }

  // --- SET defaults ---
  let finalSetCode: string = '';
  let finalSetName: string = card.set;
  let finalImageUrl: string = card.imageUrl;
  let finalCardId: number = card.cardId;

  if (autoMatchSetDetail) {
    finalSetCode = autoMatchSetDetail.code;
    finalSetName = autoMatchSetDetail.name;
    if (autoMatchSetDetail.imageUrl) {
      finalImageUrl = autoMatchSetDetail.imageUrl;
    }
    if (autoMatchSetDetail.cardId) {
      finalCardId = autoMatchSetDetail.cardId;
    }
  } else {
    const isSetCodeUUID = card.setCode && isUUID(card.setCode);
    if (!card.setCode || isSetCodeUUID) {
      if (acceptableSetDetails && acceptableSetDetails.length > 0) {
        const currentSetDetail = acceptableSetDetails.find((detail: any) => detail.name === card.set);
        if (currentSetDetail && currentSetDetail.code) {
          finalSetCode = currentSetDetail.code;
        } else {
          finalSetCode = acceptableSetDetails[0].code;
        }
      } else {
        const mapped = getSetCode(card.set);
        if (mapped && mapped !== card.set) {
          finalSetCode = mapped;
        } else {
          finalSetCode = card.set;
        }
      }
    } else {
      finalSetCode = card.setCode;
    }
  }

  // --- FOIL defaults ---
  const acceptsFoilOnly = card.acceptFoil && !card.acceptNonFoil;
  const acceptsNonFoilOnly = !card.acceptFoil && card.acceptNonFoil;

  let defaultFoil: boolean;
  if (haveMatch?.foil !== undefined) {
    if (haveMatch.foil && !acceptsNonFoilOnly) {
      defaultFoil = true;
    } else if (!haveMatch.foil && !acceptsFoilOnly) {
      defaultFoil = false;
    } else {
      defaultFoil = acceptsFoilOnly ? true :
                    acceptsNonFoilOnly ? false :
                    (hasNonFoil ? false : (hasFoil ? true : card.foil));
    }
  } else {
    defaultFoil = acceptsFoilOnly ? true :
                  acceptsNonFoilOnly ? false :
                  (hasNonFoil ? false : (hasFoil ? true : card.foil));
  }

  // --- CONDITION defaults ---
  const acceptableConditions = card.acceptableConditions;
  let defaultCondition = acceptableConditions?.[0] || 'NEAR_MINT';
  if (haveMatch?.condition) {
    if (!acceptableConditions || acceptableConditions.length === 0 ||
        acceptableConditions.includes(haveMatch.condition)) {
      defaultCondition = haveMatch.condition;
    }
  }

  // Calculate initial value based on defaults
  const initialValue = defaultFoil
    ? (card.foilPointValue || card.pointValue)
    : (card.nonFoilPointValue || card.pointValue);

  return {
    id: card.id,
    cardId: finalCardId,
    qty: card.quantity,
    maxQty: card.quantity,
    name: card.name,
    set: finalSetName,
    setCode: finalSetCode || card.set,
    condition: defaultCondition,
    language: card.language,
    foil: defaultFoil,
    edition: card.edition || '',
    image: finalImageUrl,
    imageSmall: finalImageUrl?.replace('/large/', '/small/').replace('/normal/', '/small/'),
    value: initialValue,
    baseValue: card.pointValue,
    foilValue: card.foilPointValue,
    nonFoilValue: card.nonFoilPointValue,
    acceptableSets: card.acceptableSets,
    acceptableSetCodes: (card as any).acceptableSetCodes,
    acceptableSetDetails: acceptableSetDetails,
    wantListId: card.wantListId,
    availableInFoil: hasFoil,
    availableInNonFoil: hasNonFoil,
    acceptFoil: card.acceptFoil,
    acceptNonFoil: card.acceptNonFoil,
    acceptableConditions: card.acceptableConditions,
  };
}
